import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaArrowLeft, FaTag } from 'react-icons/fa';
import { API_BASE } from '../config';
import './DetailPage.css';

const setMeta = (item) => {
  document.title = item.metaTitle || `${item.title} | Dihiz`;

  const setOrCreate = (attr, attrVal, content) => {
    let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
    if (!el) { el = document.createElement('meta'); el.setAttribute(attr, attrVal); document.head.appendChild(el); }
    el.setAttribute('content', content);
  };

  setOrCreate('name', 'description', item.metaDescription || item.summary || item.description || '');
  setOrCreate('name', 'keywords', item.metaKeywords || '');
  setOrCreate('property', 'og:title', item.metaTitle || item.title);
  setOrCreate('property', 'og:description', item.metaDescription || item.summary || item.description || '');
  if (item.image) setOrCreate('property', 'og:image', item.image);
};

const AnnouncementView = ({ item }) => (
  <div className="detail-page">
    <section className="detail-hero">
      {item.image && (
        <div className="detail-hero-bg" style={{ backgroundImage: `url(${item.image})` }}></div>
      )}
      <div className="detail-hero-overlay"></div>
      <div className="detail-hero-content">
        {item.category && (
          <span className="detail-hero-badge">
            <FaTag /> {item.category}
          </span>
        )}
        <h1 className="detail-hero-title">{item.title}</h1>
        {item.date && (
          <div className="detail-hero-meta">
            <FaCalendarAlt /> {item.date}
          </div>
        )}
      </div>
    </section>

    <section className="detail-body">
      <div className="detail-container">
        <Link to="/duyurular" className="detail-back-link">
          <FaArrowLeft /> Tüm Duyurular
        </Link>

        <div className="detail-content">
          {item.summary && <p className="detail-summary">{item.summary}</p>}
          {item.content ? (
            <div className="detail-text" dangerouslySetInnerHTML={{ __html: item.content }} />
          ) : (
            item.summary && <p className="detail-text">{item.summary}</p>
          )}
        </div>
      </div>
    </section>
  </div>
);

const EventView = ({ item }) => (
  <div className="detail-page">
    <section className="detail-hero">
      {item.image && (
        <div className="detail-hero-bg" style={{ backgroundImage: `url(${item.image})` }}></div>
      )}
      <div className="detail-hero-overlay"></div>
      <div className="detail-hero-content">
        <span className={`detail-hero-badge ${item.status === 'past' ? 'detail-hero-badge--muted' : ''}`}>
          {item.status === 'upcoming' ? 'Yaklaşan Etkinlik' : 'Tamamlanan Etkinlik'}
        </span>
        <h1 className="detail-hero-title">{item.title}</h1>
        <div className="detail-hero-meta-row">
          {item.date && <span><FaCalendarAlt /> {item.date}</span>}
          {item.time && <span><FaClock /> {item.time}</span>}
          {item.location && <span><FaMapMarkerAlt /> {item.location}</span>}
        </div>
      </div>
    </section>

    <section className="detail-body">
      <div className="detail-container">
        <Link to="/etkinlikler" className="detail-back-link">
          <FaArrowLeft /> Tüm Etkinlikler
        </Link>

        <div className="detail-info-cards">
          {item.date && (
            <div className="detail-info-card">
              <FaCalendarAlt />
              <div>
                <span className="detail-info-label">Tarih</span>
                <span className="detail-info-value">{item.date}</span>
              </div>
            </div>
          )}
          {item.time && (
            <div className="detail-info-card">
              <FaClock />
              <div>
                <span className="detail-info-label">Saat</span>
                <span className="detail-info-value">{item.time}</span>
              </div>
            </div>
          )}
          {item.location && (
            <div className="detail-info-card">
              <FaMapMarkerAlt />
              <div>
                <span className="detail-info-label">Mekan</span>
                <span className="detail-info-value">{item.location}</span>
              </div>
            </div>
          )}
        </div>

        <div className="detail-content">
          {item.description && <p className="detail-summary">{item.description}</p>}
          {item.content ? (
            <div className="detail-text" dangerouslySetInnerHTML={{ __html: item.content }} />
          ) : (
            item.description && !item.content && <p className="detail-text">{item.description}</p>
          )}
        </div>
      </div>
    </section>
  </div>
);

const PageView = ({ item }) => (
  <div className="detail-page">
    <section className="detail-hero">
      <div className="detail-hero-overlay"></div>
      <div className="detail-hero-content">
        <h1 className="detail-hero-title">{item.title}</h1>
      </div>
    </section>

    <section className="detail-body">
      <div className="detail-container">
        <div className="detail-content">
          <div className="detail-text" dangerouslySetInnerHTML={{ __html: item.content }} />
        </div>
      </div>
    </section>
  </div>
);

const SlugResolver = () => {
  const { slug } = useParams();
  const [type, setType] = useState(null);
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/resolve/${slug}`)
      .then(res => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then(result => {
        setType(result.type);
        setItem(result.data);
        setMeta(result.data);
        setLoading(false);
      })
      .catch(() => {
        setType(null);
        setItem(null);
        setLoading(false);
      });

    return () => { document.title = 'Dihiz'; };
  }, [slug]);

  if (loading) return <div className="detail-page"><div className="detail-loading">Yükleniyor...</div></div>;

  if (!item) return (
    <div className="detail-page">
      <div className="detail-not-found">
        <h2>Sayfa bulunamadı</h2>
        <Link to="/" className="detail-back-btn"><FaArrowLeft /> Ana Sayfaya Dön</Link>
      </div>
    </div>
  );

  if (type === 'announcement') return <AnnouncementView item={item} />;
  if (type === 'event') return <EventView item={item} />;
  return <PageView item={item} />;
};

export default SlugResolver;
